import React, { useState, useEffect, useMemo } from 'react'
import { Table, Panel, ButtonToolbar, IconButton, Icon, Row, Col, Dropdown } from 'rsuite'
import '../styles.less'
import TablePagination from 'rsuite/lib/Table/TablePagination'
import { tableViewSize } from '../../../utils/tableViewSizeUtil'

const basicColumns = [
  { dataKey: 'no', headerKey: 'l2No', width: 80, align: 'center' },
  { dataKey: 'budgetYear', headerKey: 'l2Budgetyear', width: 120, align: 'center' },
  { dataKey: 'contrectNo', headerKey: 'l2ContrectNo', width: 140 },
  { dataKey: 'projectCode', headerKey: 'l2ProjectCode', width: 130 },
  { dataKey: 'projectName', headerKey: 'l2ProjectName', width: 300 },
  { dataKey: 'department', headerKey: 'l2Department', width: 130 },
  { dataKey: 'coordinator', headerKey: 'l2Coordinator', width: 130 },
  { dataKey: 'granteeType', headerKey: 'l2GranteeType', width: 150 },
  { dataKey: 'organizationName', headerKey: 'l2OrganizationName', width: 280 },
  { dataKey: 'organizationAddress', headerKey: 'l2OrganizationAddress', width: 360 },
  { dataKey: 'organizationProvince', headerKey: 'l2OrganizationProvince', width: 180 },
  { dataKey: 'granteeName', headerKey: 'l2GranteeName', width: 180 },
  { dataKey: 'responsibility', headerKey: 'l2Responsibility', width: 200 },
  { dataKey: 'startDate', headerKey: 'l2StartDate', width: 140, align: 'center' },
  { dataKey: 'endDate', headerKey: 'l2EndDate', width: 180, align: 'center' },
  { dataKey: 'projectBudget', headerKey: 'l2ProjectBudget', width: 170, align: 'right' },
  { dataKey: 'projectStatus', headerKey: 'l2ProjectStatus', width: 150 },
  { dataKey: 'agreementEndDate', headerKey: 'l2AgreementEndDate', width: 240, align: 'center' },
  {
    dataKey: 'dayContractDuration',
    headerKey: 'l2DayContractDuration',
    width: 220,
    align: 'right',
  },
]

const requestColumns = [
  { dataKey: 'reqEndDate', headerKey: 'l2ReqEndDate', width: 190, align: 'center' },
  { dataKey: 'reqTotalDate', headerKey: 'l2ReqTotalDate', width: 160, align: 'right' },
  { dataKey: 'reqNoteUser', headerKey: 'l2ReqNoteUser', width: 240 },
  { dataKey: 'reqNoteCoordinator', headerKey: 'l2ReqNoteCoordinator', width: 260 },
  { dataKey: 'reqCondition', headerKey: 'l2ReqCondition', width: 180 },
  { dataKey: 'reqSubCondition', headerKey: 'l2ReqSubCondition', width: 180 },
  { dataKey: 'reqDateUser', headerKey: 'l2ReqDateUser', width: 220, align: 'center' },
  { dataKey: 'reqDateDirector', headerKey: 'l2ReqDateDirector', width: 240, align: 'center' },
]

const summarizeColumns = [
  { dataKey: 'noOfDay', headerKey: 'l2NoOfDay', width: 260, align: 'right' },
  { dataKey: 'requestCount', headerKey: 'l2RequestCount', width: 180, align: 'right' },
  { dataKey: 'requestAVG', headerKey: 'l2RequestAVG', width: 360, align: 'right' },
]

const dynamicColumnColors = [
  '#fff7d6',
  '#e9f7ef',
  '#e8f3ff',
  '#f6edff',
  '#fff0f0',
  '#eaf8f8',
  '#f3f6dc',
  '#f7eef5',
  '#edf1ff',
  '#fff1df',
]

export default function ExtentionTimePerProjectReportTable({
  schema,
  headerHeightSize,
  onRemove,
  onReload,
  pagination,
  lengthMenu,
  loading,
  Controller,
  control,
  runingNumber,
  onClickExport,
  onClickLinked,
  shaded = true,
  ...props
}) {
  let fontSize = localStorage.getItem('fontSize')

  const { Column, ColumnGroup, HeaderCell, Cell } = Table
  const [displayLength, setDisplayLength] = useState(25)
  const [page, setPage] = useState(1)
  // const [viewSize, setViewSize] = useState(schema.viewSize || [])
  const [viewSize, setViewSize] = useState([])
  const [heightSize, setHeightSize] = useState(40)

  const defaultPage = 1
  const header = schema?.header || {}
  const totalSize = Number(header.totalSize || schema?.totalSize || schema?.values?.length || 0)
  const valuesSubByProject = useMemo(() => {
    return (schema?.valuesSub || []).reduce((acc, item) => {
      const projectCode = item.projectCode

      if (!projectCode) {
        return acc
      }

      return {
        ...acc,
        [projectCode]: [...(acc[projectCode] || []), item],
      }
    }, {})
  }, [schema?.valuesSub])
  const roundMax = useMemo(() => {
    const headerRoundMax = Number(header.roundMax)
    const valuesSubMax = Object.values(valuesSubByProject).reduce((max, items) => {
      return items.length > max ? items.length : max
    }, 0)

    return Math.max(headerRoundMax || 0, valuesSubMax)
  }, [header.roundMax, valuesSubByProject])
  const roundColors = useMemo(() => {
    return Array.from({ length: roundMax }).map((_, index) => {
      return dynamicColumnColors[index % dynamicColumnColors.length]
    })
  }, [roundMax])

  useEffect(() => {
    // console.log('schema.viewSize ', schema.totalSize)
    calHeaderHeightSize()
    setViewSize(tableViewSize(totalSize))
  }, [totalSize])

  useEffect(() => {
    const schemaDisplayLength = Number(header.displayLength)

    if (schemaDisplayLength) {
      setDisplayLength(schemaDisplayLength)
    }
  }, [header.displayLength])

  const handleChangeLength = (dataKey) => {
    setPage(1)
    setDisplayLength(dataKey)
    if (typeof onReload === 'function') {
      onReload(dataKey, 1)
    }
  }

  const handleChangePage = (dataKey) => {
    setPage(dataKey)
    if (typeof onReload === 'function') {
      onReload(displayLength, dataKey)
    }
  }

  const getData = () => {
    return schema.values?.filter((v, i) => {
      const start = displayLength * (page - 1)
      const end = start + displayLength
      return i >= start && i < end
    })
  }

  const calHeaderHeightSize = () => {
    const size = fontSize <= 15 ? 40 : 80
    setHeightSize(size)
  }

  const getTableData = () => {
    return (schema?.values || []).map((row) => {
      const subRows = [...(valuesSubByProject[row.projectCode] || [])].sort((a, b) => {
        return (Number(a.seqNo) || 0) - (Number(b.seqNo) || 0)
      })

      return subRows.reduce((acc, item, index) => {
        const round = index + 1
        const requestValues = requestColumns.reduce((requestAcc, column) => {
          return {
            ...requestAcc,
            [`round${round}_${column.dataKey}`]: item[column.dataKey],
          }
        }, {})

        return {
          ...acc,
          ...requestValues,
        }
      }, row)
    })
  }

  const renderColumn = (column) => {
    return (
      <Column
        width={column.width}
        resizable={true}
        align={column.align || 'left'}
        key={column.dataKey}
      >
        <HeaderCell style={{ textAlign: 'center', ...(column.headerStyle || {}) }}>
          {column.label || header[column.headerKey] || ''}
        </HeaderCell>
        <Cell dataKey={column.dataKey} style={column.cellStyle || {}} />
      </Column>
    )
  }

  const renderSchemaColumns = () => {
    if (!schema?.tableColumns) {
      const roundColumnGroups = Array.from({ length: roundMax }).map((_, index) => {
        const round = index + 1
        const groupHeader = (header.l1NumberOftime || '').replace('X', round)
        const backgroundColor = roundColors[index]

        return (
          <ColumnGroup
            header={
              <div className="extention-time-group-header-label" style={{ backgroundColor }}>
                {groupHeader}
              </div>
            }
            key={`round-${round}`}
          >
            {requestColumns.map((column) => {
              return renderColumn({
                ...column,
                dataKey: `round${round}_${column.dataKey}`,
                headerKey: column.headerKey,
                label: (header[column.headerKey] || '').replace('X', round),
                headerStyle: { backgroundColor },
              })
            })}
          </ColumnGroup>
        )
      })

      return [
        <ColumnGroup header={header.l1Header || ''} key="basic">
          {basicColumns.map(renderColumn)}
        </ColumnGroup>,
        ...roundColumnGroups,
        <ColumnGroup header={header.l1Summarize || ''} key="summarize">
          {summarizeColumns.map(renderColumn)}
        </ColumnGroup>,
      ]
    }

    return schema.tableColumns.map((column, index) => {
      const { dataKey, label, width, resizable, align, cellStyle, idColumnGroup, columnGroup } =
        column

      if (idColumnGroup) {
        if (columnGroup) {
          return (
            <ColumnGroup header={columnGroup.header} key={index}>
              {columnGroup.columns.map((col) => {
                return (
                  <Column
                    width={col.width}
                    resizable={col.resizable || false}
                    align={col.align || 'left'}
                    key={col.dataKey}
                  >
                    <HeaderCell style={{ textAlign: 'center' }}>{col.label}</HeaderCell>
                    <Cell dataKey={col.dataKey} />
                  </Column>
                )
              })}
            </ColumnGroup>
          )
        }
      } else {
        return (
          <Column
            width={width}
            resizable={resizable || false}
            align={align || 'left'}
            key={index}
            fixed={dataKey === 'action' ? 'right' : ''}
          >
            <HeaderCell style={{ textAlign: 'center' }}>{label}</HeaderCell>

            {dataKey === 'projectObjective' || dataKey === 'action' ? (
              <Cell style={cellStyle || {}}>
                {(rowData) => {
                  if (dataKey === 'action') {
                    return (
                      <ButtonToolbar>
                        <IconButton
                          size="xs"
                          appearance="ghost"
                          icon={<Icon icon="eye" className="bt-gost-icon-radius" />}
                          onClick={() => {
                            onClickLinked(rowData?.action || null)
                          }}
                        >
                          แสดง
                        </IconButton>
                      </ButtonToolbar>
                    )
                  }
                  return <div dangerouslySetInnerHTML={{ __html: rowData.projectObjective }}></div>
                }}
              </Cell>
            ) : (
              <Cell dataKey={dataKey} style={cellStyle || {}} />
            )}
          </Column>
        )
      }
    })
  }

  const tableData = schema?.tableColumns
    ? pagination && typeof onReload !== 'function'
      ? getData()
      : schema.values || []
    : getTableData()

  return (
    <>
      <br />
      <Panel shaded={shaded} header={schema?.header?.header || ''} collapsible defaultExpanded>
        {schema.exportButton && (
          <Row>
            <Col xs={24} sm={24} md={24} lg={24} style={{ textAlign: 'right' }}>
              <IconButton
                appearance="ghost"
                size="sm"
                style={{
                  marginRight: 10,
                }}
                onClick={onClickExport}
                icon={
                  <Icon appearance="ghost" icon="file-excel-o" className="bt-gost-icon-radius" />
                }
              >
                {schema.exportButton}
              </IconButton>
            </Col>
          </Row>
        )}

        <div className="table-affix-header">
          <Table
            wordWrap
            bordered
            cellBordered
            height={schema.tableHeight || 400}
            loading={loading}
            headerHeight={headerHeightSize || heightSize * 2}
            data={tableData}
            style={{ marginTop: '20px' }}
            autoHeight
            affixHeader
          >
            {renderSchemaColumns()}
          </Table>
        </div>

        {pagination && (
          <TablePagination
            lengthMenu={lengthMenu || viewSize}
            activePage={page || defaultPage}
            renderLengthMenu={(lengthMenu) => {
              return (
                <>
                  {lengthMenu}
                  {`รายการ/หน้า`}
                </>
              )
            }}
            renderTotal={(total) => {
              return <>{` ทั้งหมด ${total} รายการ`}</>
            }}
            displayLength={displayLength || 25}
            total={totalSize}
            onChangePage={handleChangePage}
            onChangeLength={handleChangeLength}
          />
        )}
      </Panel>
    </>
  )
}
